import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAllEmotions } from '../services/api';
import VideoPlayer from '../components/VideoPlayer';
import EmotionWheel from '../components/EmotionWheel';
import LoadingSpinner from '../components/LoadingSpinner';
import { EMOTION_ORDER } from '../constants/emotions';

const TRAINING_VIDEOS = [
  { video_id: 'train1', video_filename: '01ang_Gemep.mp4' },
  { video_id: 'train2', video_filename: '05joy_Gemep.mp4' },
  { video_id: 'train3', video_filename: '07int_Gemep.mp4' },
  { video_id: 'train4', video_filename: 'A02inq112f48_X.mp4' },
  { video_id: 'train5', video_filename: 'A03fie112m54_X.mp4' },
  { video_id: 'train6', video_filename: 'A06amu123f66_X.mp4' },
  { video_id: 'train7', video_filename: 'A08col129m31_X.mp4' },
];

const CURRENT_INDEX_KEY = 'trainingCurrentIndex';
const VIDEO_FINISHED_KEY = 'trainingIsVideoFinished';

function TrainingTaskPage({ setTrainingCompleted }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [emotions, setEmotions] = useState([]);
  const [isVideoFinished, setIsVideoFinished] = useState(false);
  const [isLoadingEmotions, setIsLoadingEmotions] = useState(true);
  const [error, setError] = useState('');
  const [wheelLocked, setWheelLocked] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    localStorage.setItem('emotionSurveyCurrentTestMode', 'sound');
  }, []);

  const fetchEmotions = useCallback(async () => {
    setIsLoadingEmotions(true);
    try {
      const emotionsResponse = await getAllEmotions();
      let fetchedEmotions = emotionsResponse.data || [];

      fetchedEmotions.sort((a, b) => {
        const indexA = EMOTION_ORDER.indexOf(a.emotion_code);
        const indexB = EMOTION_ORDER.indexOf(b.emotion_code);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });

      setEmotions(fetchedEmotions);
    } catch (err) {
      console.error('Error fetching emotions: ', err);
      setError('Не удалось загрузить список эмоций.');
    } finally {
      setIsLoadingEmotions(false);
    }
  }, []);

  useEffect(() => {
    fetchEmotions();
  }, [fetchEmotions]);
  
  useEffect(() => {
	const params = new URLSearchParams(location.search);
    const urlIndex = params.get('index');
    const storedIndex = localStorage.getItem(CURRENT_INDEX_KEY);
    const storedFinished = localStorage.getItem(VIDEO_FINISHED_KEY);

     if (urlIndex !== null) {
      const parsedIndex = parseInt(urlIndex, 10);
      if (!isNaN(parsedIndex)) {
        setCurrentIndex(parsedIndex);
      }
    } else if (storedIndex !== null) {
      const parsedIndex = parseInt(storedIndex, 10);
      if (!isNaN(parsedIndex)) {
        setCurrentIndex(parsedIndex);
      }
    }

    if (storedFinished !== null) {
      setIsVideoFinished(storedFinished === 'true');
    }
  }, [location.search]);

  useEffect(() => {
    localStorage.setItem(CURRENT_INDEX_KEY, currentIndex.toString());
  }, [currentIndex]);

  useEffect(() => {
    localStorage.setItem(VIDEO_FINISHED_KEY, isVideoFinished.toString());
  }, [isVideoFinished]);

  const handleEmotionSelect = () => {
    if (!isVideoFinished || wheelLocked) {
      if (!isVideoFinished) {
        setError('Пожалуйста, дождитесь окончания просмотра видео перед выбором эмоции.');
      }
      return;
    }
    setError('');

    setWheelLocked(true);

    setTimeout(() => {
	  setWheelLocked(false);
      setIsVideoFinished(false);
      if (currentIndex < TRAINING_VIDEOS.length - 1) {
        setCurrentIndex((prev) => prev + 1);
      } else {
        if (setTrainingCompleted) {
          setTrainingCompleted(true);
        }
        localStorage.removeItem(CURRENT_INDEX_KEY);
        localStorage.removeItem(VIDEO_FINISHED_KEY);
        localStorage.setItem('emotionSurveyTrainingCompleted', 'true');
        localStorage.removeItem('emotionSurveyCurrentTestMode');
        navigate('/test-selection');
      }
    }, 1000);
  };

  const handleVideoEnd = () => {
    setIsVideoFinished(true);
  };

  if (isLoadingEmotions) {
    return (
      <div className="page-container">
        <LoadingSpinner />
        <p>Загрузка списка эмоций...</p>
      </div>
    );
  }

  const currentVideo = TRAINING_VIDEOS[currentIndex];

  return (
    <div className="page-container task-page">
      <div className="task-header">
        <h2>Тренировка №{currentIndex + 1}</h2>
        <p>Просмотрите видео и выберите эмоцию из списка. Видео запустится автоматически.</p>
      </div>

      <div className="video-player-section">
		{!isVideoFinished ? (
          <VideoPlayer
            videoFilename={currentVideo.video_filename}
            videoKey={currentVideo.video_id}
            onVideoEnd={handleVideoEnd}
          />
        ) : (
          <EmotionWheel
            emotions={emotions}
            onSelectEmotion={handleEmotionSelect}
            disabled={wheelLocked || !isVideoFinished}
          />
        )}
      </div>

      {error && <p className="error-message task-page-error">{error}</p>}

	   <div className="navigation-buttons">
        <button onClick={() => navigate(`/emotion-list?index=${currentIndex}`)}>Список эмоций</button>
      </div>
    </div>
  );
}

export default TrainingTaskPage;